/**
 * POS helper: filter by selected Counter Type (Con. Area).
 *
 * Schedule + FNB store SEPARATE counter types:
 *   scope "schedule" → schedule_counter_type / schedule_counter_type_id
 *   scope "fnb"      → fnb_counter_type / fnb_counter_type_id
 *
 * Schedules: Screen Master → screens.fnb_counter_id
 * FNB items: Edit FNB Item → Con. Area (fnb_items.counter_type_ids)
 *
 * If nothing selected for that scope → return all (no filter).
 *
 * TO DISABLE schedules: comment call in posBooking.vue
 * TO DISABLE FNB items: comment call in posFnb.vue (applyPosSectionFilter)
 */

import Api from "../services/Api";

export const COUNTER_TYPE_SCOPE = {
  SCHEDULE: "schedule",
  FNB: "fnb",
};

const COOKIE_KEYS = {
  schedule: {
    name: "schedule_counter_type",
    id: "schedule_counter_type_id",
  },
  fnb: {
    name: "fnb_counter_type",
    id: "fnb_counter_type_id",
  },
};

function normalizeScope(scope) {
  if (scope === COUNTER_TYPE_SCOPE.FNB || scope === "fnb") {
    return COUNTER_TYPE_SCOPE.FNB;
  }
  return COUNTER_TYPE_SCOPE.SCHEDULE;
}

function readCookieValue(cookieName) {
  if (typeof document === "undefined" || !cookieName) return "";
  const cookies = document.cookie.split(";").map((item) => item.trim());
  const match = cookies.find((item) => item.startsWith(`${cookieName}=`));
  if (!match) return "";
  return decodeURIComponent(match.split("=")[1] || "");
}

function writeCookie(name, value, expires) {
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires.toUTCString()}; path=/`;
}

function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

/**
 * Read counter type for a scope.
 * No legacy shared-cookie fallback — "All" for schedule/FNB must stay unfiltered.
 */
export function getCounterTypeFromCookie(scope = COUNTER_TYPE_SCOPE.SCHEDULE) {
  const resolvedScope = normalizeScope(scope);
  const keys = COOKIE_KEYS[resolvedScope];

  const counter_type = readCookieValue(keys.name);
  const counter_type_id = readCookieValue(keys.id);

  return { counter_type, counter_type_id, scope: resolvedScope };
}

export function setCounterTypeCookie(counterType, scope = COUNTER_TYPE_SCOPE.SCHEDULE) {
  if (typeof document === "undefined" || !counterType) return;
  const keys = COOKIE_KEYS[normalizeScope(scope)];
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  writeCookie(keys.name, counterType.counter_type || "", expires);
  writeCookie(keys.id, counterType.fct_id, expires);
  // Prevent old shared cookies from leaking across scopes
  clearCookie("counter_type");
  clearCookie("counter_type_id");
}

export function clearCounterTypeCookie(scope = COUNTER_TYPE_SCOPE.SCHEDULE) {
  if (typeof document === "undefined") return;
  const keys = COOKIE_KEYS[normalizeScope(scope)];
  clearCookie(keys.name);
  clearCookie(keys.id);
  // Also clear legacy shared cookies so "All" truly means no filter
  clearCookie("counter_type");
  clearCookie("counter_type_id");
}

/**
 * Clear POS local caches tied to counter-type filtering.
 * Call whenever counter type changes (including "All").
 * Server Redis purge is fire-and-forget so UI refetch is not blocked.
 */
export function clearCounterTypeRelatedCache(scope = COUNTER_TYPE_SCOPE.SCHEDULE) {
  if (typeof window === "undefined" || !window.localStorage) {
    return Promise.resolve();
  }
  const resolvedScope = normalizeScope(scope);
  try {
    if (resolvedScope === COUNTER_TYPE_SCOPE.FNB) {
      window.localStorage.removeItem("fnb_items_array");
      window.localStorage.removeItem("fnb_categories");
      // Do not await — purge can race/rename tags and delay the FNB refetch
      Api()
        .get("/cache/purge-tags/fnb_items")
        .catch(() => {});
    } else {
      window.localStorage.removeItem("showDataCache");
      window.localStorage.removeItem("datesArray");
    }
  } catch (e) {}
  return Promise.resolve();
}

export function hasCounterTypeSelected(counterTypeId) {
  if (counterTypeId == null) return false;
  const value = String(counterTypeId).trim().toLowerCase();
  if (!value) return false;
  if (value === "null" || value === "undefined" || value === "0" || value === "nan") {
    return false;
  }
  return true;
}

export function scheduleMatchesCounterTypeConArea(show, counterTypeId) {
  if (!show || !hasCounterTypeSelected(counterTypeId)) return false;
  if (show.fnb_counter_id == null || show.fnb_counter_id === "") return false;
  return String(show.fnb_counter_id) === String(counterTypeId);
}

/**
 * Filter schedules using the SCHEDULE counter-type scope.
 */
export function filterSchedulesByCounterTypeConArea(
  records = [],
  counterTypeId = null
) {
  if (!Array.isArray(records) || !records.length) {
    return records || [];
  }

  const cookie = getCounterTypeFromCookie(COUNTER_TYPE_SCOPE.SCHEDULE);
  const resolvedCounterTypeId =
    counterTypeId != null ? counterTypeId : cookie.counter_type_id;

  if (!hasCounterTypeSelected(resolvedCounterTypeId)) {
    return records;
  }

  return records.filter((show) =>
    scheduleMatchesCounterTypeConArea(show, resolvedCounterTypeId)
  );
}

function getItemCounterTypeIds(item) {
  if (!item) return [];
  const raw = item.counter_type_ids;
  if (Array.isArray(raw)) {
    return raw.map((id) => String(id).trim()).filter(Boolean);
  }
  if (raw == null || raw === "") return [];
  return String(raw)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

export function fnbItemMatchesCounterTypeConArea(item, counterTypeId) {
  if (!item || !hasCounterTypeSelected(counterTypeId)) return false;
  const ids = getItemCounterTypeIds(item);
  if (!ids.length) return false;
  return ids.some((id) => String(id) === String(counterTypeId));
}

/**
 * Filter FNB items using the FNB counter-type scope.
 */
export function filterFnbItemsByCounterTypeConArea(
  items = [],
  counterTypeId = null
) {
  if (!Array.isArray(items) || !items.length) {
    return items || [];
  }

  const cookie = getCounterTypeFromCookie(COUNTER_TYPE_SCOPE.FNB);
  const resolvedCounterTypeId =
    counterTypeId != null ? counterTypeId : cookie.counter_type_id;

  if (!hasCounterTypeSelected(resolvedCounterTypeId)) {
    return items;
  }

  return items.filter((item) =>
    fnbItemMatchesCounterTypeConArea(item, resolvedCounterTypeId)
  );
}

export const filterSchedulesForSevenStarCounter =
  filterSchedulesByCounterTypeConArea;



// WEBPACK FOOTER //
// ./src/helper/posSevenStarConfig.js